import type {
  GreetingCardSessionPayload,
  GreetingCardPayloadDecoration,
  GreetingCardDesign,
} from '@giftie/shared/greeting-cards'
import type {
  BuilderState,
  CardVariant,
  Decoration,
  UploadedPhotoMeta,
} from '~/types'

function sanitizeDecorationsForPayload(decorations: BuilderState['decorations']) {
  return {
    outside: decorations.outside.map((decoration) => {
      if (decoration.asset.source !== 'qr') return { ...decoration }

      const { src: _src, ...asset } = decoration.asset
      return {
        ...decoration,
        asset,
      }
    }),
    inside: decorations.inside.map((decoration) => {
      if (decoration.asset.source !== 'qr') return { ...decoration }

      const { src: _src, ...asset } = decoration.asset
      return {
        ...decoration,
        asset,
      }
    }),
  }
}

/**
 * Converts a serialized builder payload decoration back into the stricter
 * runtime shape needed by the editor canvas.
 */
function normalizeGreetingCardPayloadDecoration(
  side: Decoration['side'],
  decoration: GreetingCardPayloadDecoration,
): Decoration | null {
  const src = typeof decoration.asset.src === 'string'
    ? decoration.asset.src.trim()
    : ''
  const source = decoration.asset.source
  const mimeType = decoration.asset.mimeType
  const width = decoration.asset.width
  const height = decoration.asset.height

  if (!decoration.id || !decoration.transform) return null
  if (!source || !mimeType) return null
  if (typeof width !== 'number' || typeof height !== 'number') return null
  if (!src && source !== 'qr') return null
  if (!src && !decoration.asset.qrTargetUrl) return null

  return {
    id: decoration.id,
    side: decoration.side ?? side,
    locked: decoration.locked,
    transform: decoration.transform,
    asset: {
      ...decoration.asset,
      source,
      mimeType,
      width,
      height,
      src,
    },
  }
}

/**
 * Rehydrates persisted outside/inside decorations, dropping incomplete entries
 * that cannot be rendered safely in the builder.
 */
function normalizeGreetingCardPayloadDecorations(
  decorations: GreetingCardSessionPayload['decorations'],
): BuilderState['decorations'] {
  return {
    outside: Array.isArray(decorations?.outside)
      ? decorations.outside
          .map((decoration) => normalizeGreetingCardPayloadDecoration('outside', decoration))
          .filter((decoration): decoration is Decoration => decoration !== null)
      : [],
    inside: Array.isArray(decorations?.inside)
      ? decorations.inside
          .map((decoration) => normalizeGreetingCardPayloadDecoration('inside', decoration))
          .filter((decoration): decoration is Decoration => decoration !== null)
      : [],
  }
}

// Import all design files using Vite's glob import for dynamic loading
const designFiles = import.meta.glob<GreetingCardDesign[]>('../data/designs/*.json', { eager: true, import: 'default' })

const DEFAULT_INSIDE = {
  text: '',
  fontFamily: 'Roboto, Arial, sans-serif',
  fontSizePt: 22,
  resolvedFontSizePt: undefined,
  resolvedFontSizeRatio: undefined,
  fontSource: undefined,
  colorHex: '#111827',
  align: 'center' as const,
  weight: 'regular' as const,
  vAlign: 'middle' as const,
  maxChars: 600,
}

function normalizeAbsoluteUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  try {
    const parsed = new URL(trimmed)
    parsed.hash = ''
    return parsed.toString().replace(/\/+$/, '')
  } catch {
    return undefined
  }
}

function derivePlaybackBaseUrlFromHostOrigin(hostOrigin: unknown): string | undefined {
  if (typeof hostOrigin !== 'string') return undefined
  const trimmed = hostOrigin.trim()
  if (!trimmed) return undefined

  try {
    const parsed = new URL(trimmed)
    return `${parsed.origin}/apps/giftie/play`
  } catch {
    return undefined
  }
}

export function useBuilder() {
  const { fontFamily } = useTheme()
  const {
    defaultInsideFontFamily,
    insideTextFontMode,
  } = useBuilderFeatures()
  const route = useRoute()
  const initialFontFamily = computed(() =>
    defaultInsideFontFamily.value
    || fontFamily.value
    || DEFAULT_INSIDE.fontFamily,
  )

  const initialPlaybackBaseUrl = computed(() =>
    normalizeAbsoluteUrl(route.query.playbackBaseUrl)
    || derivePlaybackBaseUrlFromHostOrigin(route.query.hostOrigin),
  )

  function createInitialState(): BuilderState {
    return {
      step: 'gallery',
      frontTextLayers: {},
      frontTextResolvedFontSizeRatios: {},
      decorations: {
        outside: [],
        inside: [],
      },
      inside: {
        ...DEFAULT_INSIDE,
        fontFamily: initialFontFamily.value,
      },
      playbackBaseUrl: initialPlaybackBaseUrl.value,
    }
  }

  const state = useState<BuilderState>('builder', () => createInitialState())

  watch(
    [defaultInsideFontFamily, fontFamily, insideTextFontMode],
    ([defaultFontFamily, themeFontFamily, fontMode]) => {
      if (fontMode !== 'brand_only') return

      const resolvedFontFamily = defaultFontFamily || themeFontFamily || DEFAULT_INSIDE.fontFamily
      if (resolvedFontFamily && state.value.inside.fontFamily !== resolvedFontFamily) {
        state.value.inside.fontFamily = resolvedFontFamily
      }
    },
    { immediate: true },
  )

  // Get store config to determine which designs file to load
  const { designsFile } = useStoreConfig()

  // Load designs based on store config
  const designsData = computed<GreetingCardDesign[]>(() => {
    const file = designsFile.value
    const key = `../data/designs/${file}.json`
    const designs = designFiles[key]
    if (!designs) {
      console.warn(`Designs file not found: ${key}, falling back to default`)
      const defaultKey = '../data/designs/default.json'
      return designFiles[defaultKey] || []
    }
    return designs
  })

  const designs = {
    data: designsData,
    pending: ref(false),
  }

  // -------- Local persistence (dev) --------
  const STORAGE_KEY = 'greeting-card-builder:state'

  type Persisted = {
    selectedDesignId?: string
    selectedVariantIndex?: number
    frontTextLayers?: Record<string, string>
    frontTextResolvedFontSizeRatios?: Record<string, number>
    uploadedPhoto?: UploadedPhotoMeta
    placement?: NonNullable<BuilderState['placement']>
    inside?: BuilderState['inside']
    decorations?: BuilderState['decorations']
    attachment?: BuilderState['attachment']
    playbackBaseUrl?: string
  }

  function hydrateFromStorage() {
    if (import.meta.server) return
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const saved = JSON.parse(raw) as Persisted
      if (saved.selectedDesignId) {
        const d = (designsData.value || []).find((x) => x.id === saved.selectedDesignId)
        if (d) state.value.selectedDesign = d
      }
      if (typeof saved.selectedVariantIndex === 'number')
        state.value.selectedVariantIndex = saved.selectedVariantIndex
      if (saved.frontTextLayers)
        state.value.frontTextLayers = { ...saved.frontTextLayers }
      if (saved.frontTextResolvedFontSizeRatios)
        state.value.frontTextResolvedFontSizeRatios = { ...saved.frontTextResolvedFontSizeRatios }
      if (saved.uploadedPhoto)
        state.value.uploadedPhoto = saved.uploadedPhoto
      if (saved.placement)
        state.value.placement = saved.placement
      if (saved.inside)
        state.value.inside = { ...state.value.inside, ...saved.inside }
      if (saved.decorations) {
        state.value.decorations = {
          outside: Array.isArray(saved.decorations.outside) ? saved.decorations.outside : [],
          inside: Array.isArray(saved.decorations.inside) ? saved.decorations.inside : [],
        }
      }
      if (saved.attachment)
        state.value.attachment = saved.attachment
      if (saved.playbackBaseUrl)
        state.value.playbackBaseUrl = saved.playbackBaseUrl
    } catch {}
  }

  function persistToStorage() {
    if (import.meta.server) return
    try {
      const payload: Persisted = {
        selectedDesignId: state.value.selectedDesign?.id,
        selectedVariantIndex: state.value.selectedVariantIndex,
        frontTextLayers: state.value.frontTextLayers,
        frontTextResolvedFontSizeRatios: state.value.frontTextResolvedFontSizeRatios,
        uploadedPhoto: state.value.uploadedPhoto,
        placement: state.value.placement,
        inside: state.value.inside,
        decorations: state.value.decorations,
        attachment: state.value.attachment,
        playbackBaseUrl: state.value.playbackBaseUrl,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {}
  }

  function persistBuilderState() {
    persistToStorage()
  }

  // Track if we've already hydrated (per-session flag)
  const hasHydrated = useState('builder:hydrated', () => false)

  // Hydrate on client first usage and then watch for changes
  if (import.meta.client) {
    // Hydrate only once per session to avoid overwriting user selections during navigation
    if (!hasHydrated.value) {
      hasHydrated.value = true
      hydrateFromStorage()
    }
    // Persist deeply with simple debounce
    let t: ReturnType<typeof setTimeout>
    watch(
      () => state.value,
      () => {
        clearTimeout(t)
        t = setTimeout(persistToStorage, 150)
      },
      { deep: true },
    )
  }

  function selectDesign(design: GreetingCardDesign) {
    applyExternalDesign(design)
  }

  /**
   * Seeds template-owned inside defaults without overwriting shopper-authored text.
   */
  function seedInsideTemplateDefaults(selected?: CardVariant) {
    if (!selected?.inside) return

    if (
      selected.inside.defaultMessage != null
      && state.value.inside.text.trim().length === 0
    ) {
      state.value.inside.text = selected.inside.defaultMessage
    }

    if (selected.inside.maxChars) {
      state.value.inside.maxChars = selected.inside.maxChars
    }
  }

  function applyExternalDesign(
    design: GreetingCardDesign,
    variantIndex = 0,
    options?: { reset?: boolean },
  ) {
    if (options?.reset) {
      state.value = createInitialState()
    }

    state.value.selectedDesign = design
    state.value.selectedVariantIndex = variantIndex
    state.value.frontTextResolvedFontSizeRatios = {}
    state.value.step = 'front'

    // seed front text defaults
    const selected = design.variants?.[variantIndex]
    if (selected?.front?.textLayers) {
      for (const layer of selected.front.textLayers) {
        if (layer.defaultValue && !state.value.frontTextLayers[layer.id]) {
          state.value.frontTextLayers[layer.id] = layer.defaultValue
        }
      }
    }

    seedInsideTemplateDefaults(selected)

    if (design.decorations) {
      state.value.decorations = normalizeGreetingCardPayloadDecorations(design.decorations)
    }
  }

  function applyPersistedPayload(
    design: GreetingCardDesign,
    payload: GreetingCardSessionPayload,
    variantIndex = 0,
  ) {
    state.value = createInitialState()
    applyExternalDesign(design, variantIndex)

    if (payload.frontTextLayers) {
      state.value.frontTextLayers = { ...payload.frontTextLayers }
    }

    if (payload.frontTextResolvedFontSizeRatios) {
      state.value.frontTextResolvedFontSizeRatios = {
        ...payload.frontTextResolvedFontSizeRatios,
      }
    }

    if (payload.inside) {
      state.value.inside = {
        ...state.value.inside,
        ...payload.inside,
      }
    }

    if (payload.decorations) {
      state.value.decorations = normalizeGreetingCardPayloadDecorations(payload.decorations)
    }

    if (payload.attachment) {
      state.value.attachment = { ...payload.attachment }
    }

    if (
      payload.photo?.imageUrl
      && typeof payload.photo.width === 'number'
      && typeof payload.photo.height === 'number'
    ) {
      state.value.uploadedPhoto = {
        url: payload.photo.imageUrl,
        width: payload.photo.width,
        height: payload.photo.height,
      }
      if (payload.photo.placement) {
        state.value.placement = { ...payload.photo.placement }
      }
    }

    if (payload.playbackBaseUrl) {
      setPlaybackBaseUrl(payload.playbackBaseUrl)
    }

    persistToStorage()
  }

  const selectedVariant = computed<CardVariant | undefined>(() => {
    const d = state.value.selectedDesign
    if (!d) return undefined
    const idx = state.value.selectedVariantIndex ?? 0
    return d.variants[idx]
  })

  function setUploadedPhoto(meta: UploadedPhotoMeta) {
    state.value.uploadedPhoto = meta
  }

  function setPlacement(update: Partial<NonNullable<BuilderState['placement']>>) {
    state.value.placement = { scale: 1, rotationDeg: 0, offsetX: 0, offsetY: 0, ...state.value.placement, ...update }
  }

  function setInside(update: Partial<BuilderState['inside']>) {
    state.value.inside = { ...state.value.inside, ...update }
  }

  function setFrontTextResolvedFontSizeRatio(layerId: string, value: number | null | undefined) {
    const next = { ...state.value.frontTextResolvedFontSizeRatios }
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      next[layerId] = value
    } else {
      const { [layerId]: _value, ...withoutLayer } = next
      state.value.frontTextResolvedFontSizeRatios = withoutLayer
      return
    }
    state.value.frontTextResolvedFontSizeRatios = next
  }

  function setAttachment(att?: BuilderState['attachment']) {
    state.value.attachment = att
  }

  function setPlaybackBaseUrl(url?: string) {
    state.value.playbackBaseUrl = normalizeAbsoluteUrl(url)
      || derivePlaybackBaseUrlFromHostOrigin(url)
      || state.value.playbackBaseUrl
  }

  function toPayload(): GreetingCardSessionPayload | undefined {
    const d = state.value.selectedDesign
    const v = selectedVariant.value
    if (!d || !v) return

    const frontTextLayers = Object.keys(state.value.frontTextLayers).length > 0
      ? { ...state.value.frontTextLayers }
      : undefined
    const frontTextLayerIds = new Set((v.front.textLayers || []).map((layer) => layer.id))
    const frontTextResolvedFontSizeRatios = Object.fromEntries(
      Object.entries(state.value.frontTextResolvedFontSizeRatios)
        .filter(([layerId, value]) => frontTextLayerIds.has(layerId) && Number.isFinite(value) && value > 0),
    )
    const inside = {
      ...state.value.inside,
    }

    const payload: GreetingCardSessionPayload = {
      schemaVersion: 2,
      designId: d.id,
      variantId: v.id,
      designType: v.front.type,
      frontTextLayers,
      frontTextResolvedFontSizeRatios:
        Object.keys(frontTextResolvedFontSizeRatios).length > 0
          ? frontTextResolvedFontSizeRatios
          : undefined,
      inside,
      decorations: sanitizeDecorationsForPayload(state.value.decorations),
      attachment: state.value.attachment
        ? { ...state.value.attachment }
        : undefined,
      playbackBaseUrl: state.value.playbackBaseUrl,
    }
    if (v.front.type === 'photo' && state.value.uploadedPhoto && state.value.placement) {
      payload.photo = {
        imageUrl: state.value.uploadedPhoto.url,
        width: state.value.uploadedPhoto.width,
        height: state.value.uploadedPhoto.height,
        placement: {
          ...state.value.placement,
        },
      }
    }
    return payload
  }

  function resetBuilder() {
    state.value = createInitialState()
    if (import.meta.client) {
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch {}
    }
  }

  return {
    state,
    designs,
    selectDesign,
    selectedVariant,
    setUploadedPhoto,
    setPlacement,
    setInside,
    setFrontTextResolvedFontSizeRatio,
    setAttachment,
    setPlaybackBaseUrl,
    toPayload,
    applyPersistedPayload,
    persistBuilderState,
    resetBuilder,
    applyExternalDesign,
  }
}
